'use client';

import { LifeProduct } from '@app/domain/model/eshop/life/LifeProduct';
import { Select } from '@app/app/_components/Input/Select/Select';
import { SelectOption } from '@app/app/_components/Input/Select/SelectOption';
import { useState, useMemo, useCallback } from 'react';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { useTranslations } from 'next-intl';
import { CloseOutlinedCircleIcon } from '@app/app/_icons/CloseOutlinedCircleIcon';
import { twJoin } from 'tailwind-merge';
import { CallbackButton } from '@app/app/_components/CallbackButton/CallbackButton';
import { Message } from '@app/app/_components/Message/Message';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { HobbiesDetailsView } from '@app/app/esales/life/_sections/steps/HobbiesStep/HobbiesDetailsView';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { ViolationMessage } from '@app/lib/form/contracts/ViolationMessage';

interface Props {
    hobbies: LifeProduct[];
    errorMessage?: ViolationMap | ViolationMessage;
    defaultValue?: number[];
    onHobbiesChange?: () => void;
}

export function HobbiesStep({ hobbies, errorMessage, defaultValue, onHobbiesChange }: Props) {
    const t = useTranslations();
    const [selectedHobbies, setSelectedHobbies] = useState<string[]>(
        defaultValue ? defaultValue.map(hobbyId => hobbyId.toString()) : [],
    );
    const [searchQuery, setSearchQuery] = useState('');

    useChangeEffect(() => {
        onHobbiesChange?.();
    }, selectedHobbies);

    const [isDetailsViewOpen, setIsDetailsViewOpen] = useState(false);

    const hobbyOptions: SelectOption[] = useMemo(
        () =>
            hobbies.map(hobby => ({
                value: hobby.code,
                label: hobby.value,
            })),
        [hobbies],
    );

    const filteredOptions = useMemo(() => {
        const availableOptions = hobbyOptions.filter(option => !selectedHobbies.includes(option.value));
        if (!searchQuery) {
            return availableOptions;
        }
        return availableOptions.filter(option => option.label.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [hobbyOptions, selectedHobbies, searchQuery]);

    const handleHobbySelect = (value: string) => {
        if (value && !selectedHobbies.includes(value)) {
            setSelectedHobbies(prev => [...prev, value]);
        }
        setSearchQuery('');
    };

    const handleRemoveHobby = (hobbyId: string) => {
        setSelectedHobbies(prev => prev.filter(id => id !== hobbyId));
    };

    const getHobbyLabel = (hobbyId: string) => {
        return hobbies.find(hobby => hobby.code === hobbyId)?.value ?? hobbyId;
    };

    const closeDetailsView = useCallback(() => {
        setIsDetailsViewOpen(false);
    }, []);

    const openDetailsView = useCallback(() => {
        setIsDetailsViewOpen(true);
    }, []);

    return (
        <div>
            <div className="mb-8">
                <Select
                    name="hobbies"
                    placeholder={t('eshop.life.hobbiesPlaceholder')}
                    label={t('eshop.life.startWritingYourHobbies')}
                    options={filteredOptions}
                    value=""
                    onChange={handleHobbySelect}
                    searchable
                    onSearch={setSearchQuery}
                    error={errorMessage}
                    noResultsHint={t('eshop.life.noHobbiesFound')}
                />

                {selectedHobbies.map((hobbyId, index) => (
                    <input key={hobbyId} type="hidden" name={`hobbies[${index}]`} value={hobbyId} />
                ))}

                {selectedHobbies.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                        {selectedHobbies.map(hobbyId => (
                            <div
                                key={hobbyId}
                                className={twJoin(
                                    'flex items-center gap-2 rounded-sm',
                                    'border border-neutral-300 bg-white',
                                    'px-4 text-sm',
                                )}>
                                <span>{getHobbyLabel(hobbyId)}</span>
                                <CallbackButton
                                    ignoreFormStatus
                                    variant="text"
                                    id={hobbyId}
                                    onClick={handleRemoveHobby}
                                    className="m-0 flex h-min w-min items-center justify-center p-0 text-neutral-500 transition-colors hover:text-red-500">
                                    <CloseOutlinedCircleIcon className="size-5" />
                                </CallbackButton>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Message variant="info" className="font-normal">
                <div dangerouslySetInnerHTML={{ __html: t.raw('eshop.life.sportsHobbiesMessage') }} />
                <TextButton onClick={openDetailsView} rightAddon={<ArrowRightIcon className="size-4" />}>
                    {t('general.readMore')}
                </TextButton>
            </Message>

            <HobbiesDetailsView open={isDetailsViewOpen} onClose={closeDetailsView} />
        </div>
    );
}
